import React, { useEffect, useRef, useState } from "react";
import styles from "./ProjectsSection.module.css";
import { featured, type Project } from "../data/featuredProjects";
import { useInView } from "../Hooks/useInView";
import { Link } from "react-router-dom";

import CustomButton from "./CustomButton";

const HERO_IMG = "/images/IMG_0362.jpeg";
const preloadImages = [
  HERO_IMG,
  ...featured.map((p) => p.previewImage || p.images[0]),
];

const ProjectsHero: React.FC = () => {
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <section
      className={`${styles.hero} ${revealed ? styles.revealed : ""}`}
      aria-label="Unsere Projekte – Hero"
    >
      <div className={styles.heroMedia} aria-hidden="true">
        <img
          src={HERO_IMG}
          alt=""
          className={styles.heroImg}
          decoding="async"
          loading="eager"
          draggable={false}
        />
        <div className={styles.heroOverlay} />
      </div>

      <div className={styles.heroText}>
        <span className={`${styles.kicker} ${styles.st1}`}>REFERENZEN</span>
        <h1 className={`${styles.title} ${styles.st2}`}>
          Vielfalt bauen – Lebensräume schaffen
        </h1>
        <p className={`${styles.lead} ${styles.st3}`}>
          Wir entwickeln Zukunft – fundiert, vertrauensvoll und werthaltig.
        </p>
      </div>
    </section>
  );
};


const IntroBlock: React.FC = () => {
  return (
    <div className={styles.intro}>
      <div className={styles.introHeader}>
        <span className={styles.vLabel} aria-hidden="true">
          REFERENZEN
        </span>
        <h2 className={styles.introH2}>Referenzen im Überblick</h2>
      </div>

      <div className={styles.introText}>
        <p>
          Bei der Dr. Fernando International GmbH entwickeln wir Immobilien, die
          so vielfältig sind wie die Menschen, die darin leben und arbeiten.
          Unser Fokus liegt auf zukunftsfähigen Konzepten, die sich an den
          realen Bedürfnissen unserer Gesellschaft orientieren – funktional,
          architektonisch anspruchsvoll und wirtschaftlich nachhaltig.
        </p>

        <p>
          <strong>Wohnquartiere mit Lebensqualität</strong> 
          <br></br>
          Wir schaffen
          moderne Wohnquartiere, die weit mehr sind als reine Wohnflächen. Mit
          durchdachter Architektur, effizienten Grundrissen und sinnvoller
          Infrastruktur entstehen Nachbarschaften, in denen sich Menschen
          langfristig zuhause fühlen.
        </p>

        <p>
          <strong>Betreutes Wohnen – mit Würde und Komfort</strong> 
          <br></br>
          In enger
          Abstimmung mit Pflege- und Betreuungsträgern entwickeln wir
          Wohnformen, die älteren Menschen Selbstbestimmung und Sicherheit
          bieten. Unsere Projekte im Bereich betreutes Wohnen verbinden
          Funktionalität mit einer hohen Aufenthaltsqualität – für ein
          würdevolles Leben im Alter.
        </p>
      </div>
    </div>
  );
};

const LeadProject: React.FC<Project> = ({
  id,
  title,
  location,
  images,
  description,
}) => {
  const [ref, inView] = useInView<HTMLDivElement>(0.1);
  const teaser = (description || "").split("\n")[0];
  return (
    <div
      ref={ref}
      className={`${styles.leadWrap} ${inView ? styles.visible : ""}`}
    >
      <Link
        to={`/projects/${id}`}
        className={styles.leadCard}
        aria-label={title}
      >
        <div className={styles.leadMedia}>
          <img
            src={images[0]}
            alt={title}
            className={styles.leadImg}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
          <div className={styles.leadGlass}>
            <div className={styles.leadMeta}>
              <h3 className={styles.leadTitle}>{title}</h3>
              <div className={styles.leadLoc}>{location}</div>
            </div>
            <div className={styles.leadCta}>
              <CustomButton
                variant="filled"
                size="small"
                text="Mehr erfahren"
                asChild
              />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

const ProjectCard: React.FC<Project & { index: number }> = ({
  id,
  title,
  location,
  images,
  index,
}) => {
  const [ref, inView] = useInView<HTMLAnchorElement>(0.2);
  return (
    <Link
      to={`/projects/${id}`}
      ref={ref}
      aria-label={title}
      className={`${styles.card} ${inView ? styles.visible : ""}`}
      style={{ ["--delay" as any]: `${index * 0.06}s` }}
    >
      <div className={styles.cardMedia}>
        <img
          src={images[0]}
          alt={title}
          className={styles.cardImg}
          loading="lazy"
          decoding="async"
          draggable={false}
        />
        <div className={styles.cardOverlay} aria-hidden="true" />
        <div className={styles.cardBar}>
          <div className={styles.cardText}>
            <h4 className={styles.cardTitle}>{title}</h4>
            <div className={styles.cardLoc}>{location}</div>
          </div>
          <div className={styles.cardCta}>
            <CustomButton
              variant="filled"
              size="small"
              text="Mehr erfahren"
              asChild
            />
          </div>
        </div>
      </div>
    </Link>
  );
};

const TailNote: React.FC = () => (
  <div className={styles.tailNote}>
    <p>
      Weitere Referenzen der Projektentwicklung finden Sie auf der Website
      unserer Tochtergesellschaft:&nbsp;
      <a
        href="https://avila-projektmanagement.de"
        target="_blank"
        rel="noopener noreferrer"
        className={styles.noteLink}
        aria-label="AVILA Projektmanagement GmbH (neues Fenster)"
      >
        AVILA Projektmanagement GmbH
      </a>
      .
    </p>
  </div>
);

const ProjectsSection: React.FC = () => {
  const lead = featured[0];
  const rest = featured.slice(1);

  return (
    <section
      className={styles.projectsPage}
      id="referenzen"
      data-section="projects"
      aria-label="Referenzen im Überblick"
    >
   
        <ProjectsHero />
        <div className={styles.inner}>
          <IntroBlock />
          {lead && <LeadProject {...lead} />}
          <div className={styles.grid}>
            {rest.map((proj, idx) => (
              <ProjectCard key={proj.id} index={idx} {...proj} />
            ))}
          </div>
          <TailNote />
        </div>
  
    </section>
  );
};

export default ProjectsSection;
